/*
 * 左上角数值面板。
 *
 * 复刻旧产品的 MyValues：羁绊等级（圆环进度）/ 好感度（心 + 称号）/ 灵力（火焰）
 * 三行，叠在半透明白底上。旧实现分散在
 *   user/MyLevel.tsx · MyReputation.tsx · MyPower.tsx · CharacterRelationModule.tsx
 * 这里合成一个组件，但**规则全部搬到服务端**：
 *
 *   - 升级曲线不在前端算。旧版 MyLevel 内联了 `30*(level³+5*level)-80`，
 *     packages/config/src/economy.ts 已经是唯一实现，前端重算一遍必然分叉。
 *     这里只画服务端给的 progress 百分比。
 *   - 称号阈值不在前端判。旧版 MyReputation 内联了
 *     100/1000/10000/100000/1000000 五档，服务端一改前端就静默错位。
 *     这里只渲染服务端下发的 stageLabel。
 *   - 灵力状态不做精确匹配。旧版用 `power === 10` / `=== 20` 判断提示文案，
 *     数值一变提示就失效。这里只区分「有」和「耗尽」。
 *
 * 拿不到值就画横杠，绝不填 0：0 会被读成「我被扣光了」。
 */

import { useId } from "react";
import { FIRE_SOLID_PATH, FILLED_VIEWBOX, IconHeartSolid } from "./Icons";

export interface RelationSnapshot {
  /** 羁绊等级。 */
  level: number | null;
  /** 距下一级的进度，0–1。服务端算好下发，前端不反推。 */
  levelProgress: number | null;
  /** 当前等级内已攒的羁绊。**不是累计总量**，升级时会减去该级消耗重新计数。 */
  bond: number | null;
  /** 本级需要多少羁绊才能升级。 */
  bondForThisLevel: number | null;
  /** 距下一级还差多少。0 表示已满级。 */
  bondToNextLevel: number | null;
  affinity: number | null;
  /** 好感阶段称号，由服务端裁定。 */
  stageLabel: string | null;
  power: number | null;
  /**
   * 灵力表盘的填充比例，0–1。服务端算好下发。
   *
   * ⚠ **不是「余额 / 上限」**——灵力没有上限。见 `PowerFlame` 的注释。
   */
  powerProgress: number | null;
}

/* -------------------------------------------------------------------------- */
/* 等级圆环                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 圆环进度。
 *
 * 旧版用 antd 的 `Progress type="circle" size={15}`，为了一个 15px 的圈拖进整个
 * 组件库。这里用十几行 SVG 复刻它的几何：外径 15px、线宽 3px、圆头端点。
 * 等级数字在环右边的「Level N」里，不塞进环内——旧版也不塞：antd 对
 * `size ≤ 20` 的圈会自动把百分比文字挪进 Tooltip（`Circle.js:69-78`）。
 *
 * ⚠ **`r` 与 `stroke-width` 是 viewBox 单位，跟着 `.level-ring` 的盒子一起缩放。**
 * 换算见 components.css 里 `.level-ring` 的注释，改盒子尺寸要连着改线宽。
 */
function LevelRing({ progress }: { progress: number | null }) {
  const r = 8;
  const circumference = 2 * Math.PI * r;
  const dash = progress === null ? 0 : circumference * Math.min(Math.max(progress, 0), 1);

  return (
    <span className="level-ring" aria-hidden>
      <svg viewBox="0 0 20 20">
        <circle className="level-ring__track" cx="10" cy="10" r={r} />
        <circle
          className="level-ring__fill"
          cx="10"
          cy="10"
          r={r}
          strokeDasharray={`${dash} ${circumference}`}
        />
      </svg>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* 灵力火焰                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 按比例填充的火焰。
 *
 * 同一条火焰形状画两遍：底下一遍压暗当「空槽」，上面一遍用 `clipPath` 裁成
 * 一个从底部往上长的矩形。**不是画一个矩形进度条再套火焰形状** —— 那样火焰
 * 收窄的尖顶处会露出直角。
 *
 * ## ⚠ 满格不等于余额上限
 *
 * 灵力**没有上限**（`POWER_BALANCE_CAP === null`，余额不过期不清零）。
 * 服务端拿 `POWER_GAUGE_FULL`（当前 100，等于新用户礼物）这条**显示参考线**
 * 算好 0~1 下发，超出即截断。
 *
 * 这条表盘的用途是**快用完时预警**，不是展示财富：满格之上的差别
 * （120 和 660）对用户没有决策价值，而接近 0 的那一段有。
 *
 * ⚠ **别因为「实测大家都在 100 附近」就把 100 当真实上限内联到这里。**
 * 历史数据里绝大多数人停在新用户礼物那一格；充值到账后可以自然超过它。
 * 比例一律由服务端给，理由与 `LevelRing` 那条一致（见文件头）。
 *
 * ## clipPath 的 id 必须每个实例唯一
 *
 * SVG 的 id 是**整个文档**范围的，不是组件范围的。写死一个字符串的话，
 * 页面上出现第二个火焰时两者会共用先挂载的那个 clip，后一个的比例静默失效。
 * `useId()` 给的是 React 保证唯一的字符串。
 */
function PowerFlame({ progress }: { progress: number | null }) {
  const clipId = useId();
  // 拿不到比例时画满，不画空：空火焰读起来是「你没灵力了」，那是在编数字。
  // 真的没有灵力时上层会走 is-drained 那一支，根本不显示数值。
  const ratio = progress === null ? 1 : Math.min(Math.max(progress, 0), 1);

  // viewBox 是 antd 那套 64 64 896 896，所以底边是 960、可见高度 896。
  const top = 64 + 896 * (1 - ratio);

  return (
    <svg
      className="hud__icon power-flame"
      viewBox={FILLED_VIEWBOX}
      width="15"
      height="15"
      fill="currentColor"
      aria-hidden
      focusable={false}
    >
      <clipPath id={clipId}>
        <rect x="64" y={top} width="896" height={64 + 896 - top} />
      </clipPath>
      <path className="power-flame__track" d={FIRE_SOLID_PATH} />
      <path
        className="power-flame__fill"
        d={FIRE_SOLID_PATH}
        clipPath={`url(#${clipId})`}
      />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* 面板                                                                        */
/* -------------------------------------------------------------------------- */

/*
 * 拿不到值时画横杠，绝不填 0。
 *
 * ⚠ **这条横杠是静止的，别再给它加呼吸。**
 *
 * 它曾经带着 2.6s 的呼吸，读起来像「正在取」。但它表示的是终态：
 * `App.tsx` 在首次加载或失败时把整个快照折成全 null，而 `hasAnyValue` 会
 * 让 HUD 整块不渲染；已有数据的后台刷新会保留上一帧，避免消息区跟着跳动。
 * 横杠只在「请求成功、但某个字段服务端就是没给」时出现，那是读不到，不是在读。
 * 一个会动的东西表示一件已经结束的事，只会让人一直等。
 */
function Value({ value }: { value: number | null }) {
  if (value === null) return <span className="hud-dash" aria-label="读取不到" />;
  return <span className="hud__num">{value}</span>;
}

export function ValueHud({
  relation,
  onExplainLevel,
  onExplainPower,
}: {
  relation: RelationSnapshot;
  onExplainLevel: () => void;
  onExplainPower: () => void;
}) {
  // 只区分「有灵力」与「耗尽」，不对具体数值做匹配。
  const drained = relation.power !== null && relation.power <= 0;

  return (
    <div className="hud">
      <button
        type="button"
        className="hud__row hud__row--action"
        onClick={onExplainLevel}
      >
        <LevelRing progress={relation.levelProgress} />
        {/* 旧版这里就是英文 Level，不是「羁绊等级」。 */}
        <span className="hud__label">Level</span>
        <Value value={relation.level} />
      </button>

      <div className="hud__row">
        {/*
         * 三行的起始图标要一样宽，否则后面的文字对不齐。
         *
         * 旧版是「圆环 15px + 两个 13px 的实心双色图标」
         * （`HeartTwoTone` / `FireTwoTone`）。这里把两个图标也抬到 15px，
         * 三个盒子完全等宽；实心是照旧版来的，描边版在这个尺寸下太轻，
         * 压在背景原画上几乎看不见。
         */}
        <span className="hud__glyph" aria-hidden>
          <IconHeartSolid size={15} className="hud__icon" />
        </span>
        <span className="hud__label">好感度</span>
        <Value value={relation.affinity} />
        {relation.stageLabel ? (
          <span className="hud__tag">{relation.stageLabel}</span>
        ) : null}
      </div>

      <button
        type="button"
        className={`hud__row hud__row--action${drained ? " is-drained" : ""}`}
        onClick={onExplainPower}
      >
        <span className="hud__glyph" aria-hidden>
          {/* 旧版这里是火焰不是水滴（`FireTwoTone`），看着像水滴是因为粉色。 */}
          <PowerFlame progress={relation.powerProgress} />
        </span>
        <span className="hud__label">{drained ? "灵力微弱" : "灵力"}</span>
        {drained ? null : <Value value={relation.power} />}
      </button>
    </div>
  );
}
